import { InteractionStatus } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import { useEffect, useRef, useState } from "react";
import { CRE_SELF_REGISTER_URL } from "./creExtensionConstants";
import { setCreLoginIntent } from "./creLoginIntent";
import { setMedmicReturnRoute } from "./creMedmicReturnRoute";
import { getCreMsalInstance } from "./creMsalInstance";
import { markMedMicExplicitConnect } from "./medmicExplicitConnect";
import { MedMicAuthCoordinator } from "./medmicAuthCoordinator";
import { startMedmicInteractiveSignIn } from "./medmicSignIn";
import { CRE_EXTENSION_PATH } from "../constants/routes";

export default function CreExtensionLoginPage() {
  const { inProgress } = useMsal();
  const loading = inProgress !== InteractionStatus.None;
  const [micPermission, setMicPermission] = useState("unknown");
  const [micStatus, setMicStatus] = useState("");
  const micPermissionStatusRef = useRef(null);

  const refreshMicPermission = async () => {
    if (micPermissionStatusRef.current) {
      micPermissionStatusRef.current.onchange = null;
      micPermissionStatusRef.current = null;
    }
    try {
      const result = await navigator.permissions.query({
        name: "microphone",
      });
      micPermissionStatusRef.current = result;
      setMicPermission(result.state);
      result.onchange = () => setMicPermission(result.state);
    } catch {
      setMicPermission("unknown");
    }
  };

  useEffect(() => {
    void refreshMicPermission();
    return () => {
      if (micPermissionStatusRef.current) {
        micPermissionStatusRef.current.onchange = null;
      }
    };
  }, []);

  const handleGiveMicPermission = async () => {
    setMicStatus("Requesting microphone permission...");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setMicPermission("granted");
      setMicStatus("Microphone permission granted.");
    } catch (error) {
      const message =
        error instanceof Error ? error.message : "Microphone permission denied.";
      setMicStatus(`Could not grant microphone permission: ${message}`);
      void refreshMicPermission();
    }
  };

  const handleSignIn = () => {
    setCreLoginIntent("extension");
    setMedmicReturnRoute(window.location.pathname);
    markMedMicExplicitConnect();
    const instance = getCreMsalInstance();
    const accounts = instance.getAllAccounts?.() ?? [];
    if (accounts.length > 0) {
      void MedMicAuthCoordinator.connect({ source: "login_page_connect" }).then(
        (ok) => {
          if (ok) {
            window.location.assign(
              `${window.location.origin}${CRE_EXTENSION_PATH}/handoff`,
            );
            return;
          }
          // Saved account but the silent connect failed (e.g. expired
          // sign-in): the user asked to connect, so sign in interactively
          // instead of leaving them on an error with no way out.
          void startMedmicInteractiveSignIn(instance);
        },
      );
      return;
    }
    void startMedmicInteractiveSignIn(instance);
  };

  return (
    <div className="cre-root cre-login-page">
      <div className="cre-login-bg" aria-hidden />
      <div className="cre-login-grid" aria-hidden />
      <div className="cre-login-card">
        <h1 className="cre-login-title">MedMic</h1>
        <p className="cre-login-sub">
          Sign in to use MedMic with your Microsoft account.
        </p>
        <div className="cre-login-actions">
          <button
            type="button"
            className="cre-btn-signin"
            disabled={loading}
            onClick={handleSignIn}
          >
            {loading ? "Signing in…" : "Connect MedMic"}
          </button>
          <button
            type="button"
            id="cre-open-pip-btn"
            className="cre-btn-outline"
          >
            Open floating panel
          </button>
        </div>
        <div className="cre-login-help">
          <p className="cre-login-note">
            Already signed in? Open floating panel and keep this tab pinned for
            always-on-top mode.
          </p>
          <p className="cre-login-note">
            If floating window does not open,{" "}
            <button
              type="button"
              className="cre-btn-text-inline"
              onClick={() => window.location.reload()}
            >
              reload this page
            </button>
            .
          </p>
        </div>
        {micPermission !== "granted" && (
          <div className="cre-login-help">
            <p className="cre-login-note">
              If dictation microphone does not work, give browser microphone permission.
            </p>
            <button
              type="button"
              className="cre-btn-outline"
              onClick={() => void handleGiveMicPermission()}
            >
              Give microphone permission
            </button>
            {micStatus && (
              <p className="cre-login-note">
                {micStatus}
              </p>
            )}
          </div>
        )}
        <p className="cre-login-note cre-login-links">
          New here?{" "}
          <a
            href={CRE_SELF_REGISTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "var(--cre-accent)" }}
          >
            Create an account at Back Office
          </a>
          {" · "}
          <a
            href={CRE_SELF_REGISTER_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: "var(--cre-accent)" }}
          >
            Forgot password
          </a>
        </p>
      </div>
    </div>
  );
}
